import React, { useState, useEffect, useRef } from 'react';
import { DIRECTUS_INSTANCE } from '@/config/env';
import Skeleton from './Skeleton';

interface ThumbnailProps {
  thumbnailId?: string | null;
  fallbackIcon?: React.ReactNode;
  className?: string;
}

/**
 * Thumbnail component that loads images from Directus
 * Falls back to provided icon if thumbnailId is not available or image fails to load
 * Optimized with memoization and proper state reset
 */
const Thumbnail = React.memo(function Thumbnail({ thumbnailId, fallbackIcon, className = '' }: ThumbnailProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const currentThumbnailIdRef = useRef<string | null | undefined>(null);
  const retryCountRef = useRef(0);

  // Reset state when thumbnailId changes
  useEffect(() => {
    const normalizedThumbnailId = thumbnailId ?? null;
    if (currentThumbnailIdRef.current !== normalizedThumbnailId) {
      currentThumbnailIdRef.current = normalizedThumbnailId;
      setImageError(false);
      setImageLoading(true);
      retryCountRef.current = 0;
      // Reset img src to force reload
      if (imgRef.current) {
        imgRef.current.src = '';
      }
    }
  }, [thumbnailId]);

  // Set image URL when thumbnailId changes - optimized with WebP and specific size
  useEffect(() => {
    if (thumbnailId) {
      // Use Directus transform: width=64, height=48 (4:3 ratio), fit=cover, format=webp for fast loading
      const optimizedUrl = `${DIRECTUS_INSTANCE}/assets/${thumbnailId}?width=64&height=48&fit=cover&format=webp&quality=75`;
      setCurrentImageUrl(optimizedUrl);
    } else {
      setCurrentImageUrl(null);
    }
  }, [thumbnailId]);

  // If no thumbnail ID, show fallback immediately
  if (!thumbnailId || !currentImageUrl) {
    return (
      <div className={`w-16 h-12 flex-shrink-0 rounded overflow-hidden bg-bg-control border border-border flex items-center justify-center ${className}`}>
        {fallbackIcon}
      </div>
    );
  }

  const handleLoad = () => {
    setImageLoading(false);
    setImageError(false);
    retryCountRef.current = 0;
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Try without webp format if webp fails (fallback to original format)
    if (retryCountRef.current === 0 && imgRef.current && currentImageUrl?.includes('format=webp')) {
      retryCountRef.current = 1;
      const fallbackUrl = `${DIRECTUS_INSTANCE}/assets/${thumbnailId}?width=64&height=48&fit=cover&quality=75`;
      setCurrentImageUrl(fallbackUrl);
      setImageLoading(true);
      setImageError(false);
      // Force reload by setting src
      imgRef.current.src = fallbackUrl;
    } else {
      setImageError(true);
      setImageLoading(false);
    }
  };

  return (
    <div className={`w-16 h-12 flex-shrink-0 rounded overflow-hidden bg-bg-control border border-border flex items-center justify-center relative ${className}`}>
      {/* Skeleton loader while loading */}
      {imageLoading && !imageError && (
        <Skeleton className="absolute inset-0 w-full h-full" />
      )}
      
      {/* Fallback icon on error */}
      {imageError && fallbackIcon && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          {fallbackIcon}
        </div>
      )}
      
      {/* Actual image */}
      {!imageError && currentImageUrl && (
        <img
          ref={imgRef}
          src={currentImageUrl}
          alt=""
          className={`w-full h-full object-cover ${imageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
        />
      )}
    </div>
  );
});

export default Thumbnail;
